import type { ZodIssue } from 'zod';

export type ApiErrorCode =
  | 'SCHEMA_VALIDATION_FAILED'
  | 'SESSION_EXPIRED'
  | 'FORBIDDEN'
  | 'NOT_FOUND'
  | 'CONFLICT'
  | 'VALIDATION_ERROR'
  | 'NETWORK_ERROR'
  | 'SERVER_ERROR'
  | 'UNKNOWN_ERROR';

export interface ApiErrorBody {
  message?: string;
  error?: string;
  field?: string;
  fieldErrors?: Record<string, string>;
  [k: string]: unknown;
}

export interface RedactedZodIssue {
  path: string;
  code: string;
}

interface ApiErrorArgs {
  status: number;
  code: ApiErrorCode;
  message: string;
  traceId?: string | null;
  body?: ApiErrorBody;
  zodIssues?: ZodIssue[] | null;
  sessionExpired?: boolean;
}

interface MinimalResponse {
  status: number;
  headers?: { get(name: string): string | null } | null;
}

function statusToCode(status: number): ApiErrorCode {
  if (status === 401) return 'SESSION_EXPIRED';
  if (status === 403) return 'FORBIDDEN';
  if (status === 404) return 'NOT_FOUND';
  if (status === 409) return 'CONFLICT';
  if (status === 400) return 'VALIDATION_ERROR';
  if (status >= 500) return 'SERVER_ERROR';
  return 'UNKNOWN_ERROR';
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: ApiErrorCode;
  readonly traceId: string | null;
  readonly body: ApiErrorBody;
  readonly zodIssues: RedactedZodIssue[] | null;
  readonly sessionExpired: boolean;

  constructor(args: ApiErrorArgs) {
    super(args.message);
    this.name = 'ApiError';
    this.status = args.status;
    this.code = args.code;
    this.traceId = args.traceId ?? null;
    this.body = args.body ?? {};
    this.zodIssues = args.zodIssues
      ? args.zodIssues.map((i) => ({
          path: Array.isArray(i.path) ? i.path.join('.') : String(i.path ?? ''),
          code: i.code,
        }))
      : null;
    this.sessionExpired = args.sessionExpired ?? false;
  }

  static fromResponse(
    response: MinimalResponse,
    body: ApiErrorBody,
    traceId: string | null
  ): ApiError {
    const status = response.status;
    const code = statusToCode(status);
    return new ApiError({
      status,
      code,
      message:
        body.message ?? body.error ?? `Request failed with status ${status}`,
      traceId,
      body,
      sessionExpired: status === 401,
    });
  }

  static fromZodFailure(
    endpoint: string,
    issues: ZodIssue[],
    traceId: string | null
  ): ApiError {
    return new ApiError({
      status: 200,
      code: 'SCHEMA_VALIDATION_FAILED',
      message: `Response from ${endpoint} did not match expected shape`,
      traceId,
      body: {},
      zodIssues: issues,
    });
  }

  static fromNetworkFailure(message: string): ApiError {
    return new ApiError({
      status: 0,
      code: 'NETWORK_ERROR',
      message,
      traceId: null,
    });
  }

  static fromSessionExpired(): ApiError {
    return new ApiError({
      status: 401,
      code: 'SESSION_EXPIRED',
      message: 'Your session has expired. Please sign in again.',
      sessionExpired: true,
    });
  }
}
